/**
 * The pieces.
 *
 * A piece is a hexagonal tile in its owner's colour, inset inside its cell so the board
 * reads as something you place stones on rather than something you paint. The mark is
 * knocked out of the tile rather than drawn on top, which keeps it legible at any size —
 * these get rendered at about twenty pixels across on a phone.
 *
 * The 2023 build drew a `♔` glyph for a king and nothing at all for a pawn, so at a
 * glance the board was two colours of blob.
 */

import { CORNERS } from '@gok/engine';

/** A unit hexagon, corner to corner, centred on the origin. */
export const HEX_PATH =
  CORNERS.map(({ x, y }, i) => `${i === 0 ? 'M' : 'L'}${x} ${y}`).join(' ') +
  ' Z';

/**
 * A crown: three points on a band. Drawn rather than set in a font so it keeps its
 * weight when scaled down and does not depend on what the browser has installed.
 */
const KING_MARK = [
  'M -0.42 0.12',
  'L -0.52 -0.34',
  'L -0.24 -0.10',
  'L 0 -0.42',
  'L 0.24 -0.10',
  'L 0.52 -0.34',
  'L 0.42 0.12',
  'Z',
  'M -0.40 0.26',
  'L 0.40 0.26',
  'L 0.40 0.40',
  'L -0.40 0.40',
  'Z',
].join(' ');

export function PieceMark({
  king,
  ink,
  rotate = 0,
}: {
  king: boolean;
  ink: string;
  rotate?: number;
}) {
  return (
    <g
      transform={rotate ? `rotate(${rotate})` : undefined}
      fill={ink}
      pointerEvents='none'
    >
      {king
        ? <path d={KING_MARK} fillRule='evenodd' />
        : <circle cx={0} cy={0} r={0.3} />}
    </g>
  );
}

export function PieceTile({
  king,
  player,
  scale = 0.82,
  rotate = 0,
  className = '',
}: {
  king: boolean;
  player: 0 | 1;
  scale?: number;
  rotate?: number;
  className?: string;
}) {
  return (
    <g transform={`scale(${scale})`} className={className} pointerEvents='none'>
      <path
        d={HEX_PATH}
        className={player === 0 ? 'fill-player-0' : 'fill-player-1'}
      />
      <g transform='scale(0.62)'>
        <PieceMark
          king={king}
          ink={`var(--piece-ink-${player})`}
          rotate={rotate}
        />
      </g>
    </g>
  );
}
